import { IonCard, IonSkeletonText } from '@ionic/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../../components/actions/Button'
import {
	getButtonText,
	getCurrentLabel,
	getTitle,
} from './utils'
import { formatTimePeriod, isWithinPeriod, TimePeriod, TimePeriodResolution } from '../../../../utils/timePeriod'
import dayjs from 'dayjs'
import { SpotPrice } from '../../../../hooks/dayAheadPrices/useDayAheadPrices'
import { priceString } from '../../../../utils/priceUtils'
import quarterHour from '../../../../utils/quarterHourDayjsPlugin/quarterHour'

dayjs.extend(quarterHour)

interface HourlyPriceProps {
	dayAheadPrices: SpotPrice[]
	timePeriod: TimePeriod
	dataResolution: TimePeriodResolution
	isLoading: boolean
}

const HourlyPrice = ({ dayAheadPrices, timePeriod, dataResolution, isLoading }: HourlyPriceProps) => {
	const [hidePastPrices, setHidePastPrices] = useState<boolean>(true)
	const { t } = useTranslation('pages_electricity')

	const isCurrentPeriod = isWithinPeriod(dayjs(), timePeriod)

	const renderSkeletonRows = () => {
		const rowCount = timePeriod.resolution === 'day' ? 6 : timePeriod.resolution === 'week' ? 5 : 4
		return Array(rowCount)
			.fill(0)
			.map((_, i) => (
				<div
					key={i}
					className='hourly border-b border-background-200-light dark:border-surface-dark text-text-dark dark:text-white'
				>
					<div className='w-[20%]'>
						{isCurrentPeriod && i === 0 && (
							<IonSkeletonText animated style={{ width: '100%', height: '24px' }} />
						)}
					</div>
					<div className='w-[40%]'>
						<IonSkeletonText animated style={{ width: '100%', height: '24px' }} />
					</div>
					<div className='w-[40%]'>
						<IonSkeletonText animated style={{ width: '100%', height: '24px' }} />
					</div>
				</div>
			))
	}

	return (
		<IonCard id='hour-list' className='card shadow-card bg-white dark:bg-[#0d2d30]'>
			<div id='hour-list'>
				<p className='bold tracking-wide text-text-dark dark:text-white'>{getTitle(timePeriod.resolution, t)}</p>

				{isCurrentPeriod && (
					<Button className='mb-2 mt-0' onClick={() => setHidePastPrices(state => !state)}>
						{getButtonText(!hidePastPrices, timePeriod.resolution, t)}
					</Button>
				)}

				{isLoading ? (
					<div className='relative'>{renderSkeletonRows()}</div>
				) : (
					dayAheadPrices.map((price, i) => {
						if (hidePastPrices && isCurrentPeriod && dayjs().startOf(dataResolution).isAfter(price.deliveryStart)) {
							return null
						}

						return (
							<div
								key={i}
								className='hourly border-b border-background-200-light dark:border-surface-dark text-text-dark dark:text-white'
							>
								<div className='current-hour max-w-24'>
									{isCurrentPeriod && dayjs().isSame(price.deliveryStart, dataResolution) && (
										<p className='now tracking-wide bg-brand-primary-100-light dark:bg-brand-primary-100-dark'>
											{getCurrentLabel(timePeriod.resolution, t)}
										</p>
									)}
								</div>
								<div className='time-container'>
									<span>{formatTimePeriod({ resolution: dataResolution, timestamp: price.deliveryStart })}</span>
								</div>
								<div className='price-container'>
									<span className='bold'>
										{priceString(price.centsPerKwh)}
									</span>
									<span className='price'>{t('hourlyPrice.centsPerKWh')}</span>
								</div>
							</div>
						)
					})
				)}
			</div>
		</IonCard>
	)
}

export default HourlyPrice
